/**
 * /admin/expansion — 扩散计划：资源池里的群怎么变成监听群。
 *
 *   候选 → 批准（自动按分 / 人工）→ 加入（按预算限速）→ 考核 → 保留 / 退群拉黑
 *
 * Everything the scheduler decides is visible and overridable here.
 */

import { Fragment, useMemo, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ExpansionRunResponse,
  type ExpansionStats,
  type JoinPlanStatus,
  type JoinPlanSummary,
  Routes,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { toastError, toastInfo, toastSuccess } from '../../lib/toast';

const STATUS: Record<JoinPlanStatus, { text: string; cls: string }> = {
  candidate: { text: '候选', cls: 'badge-neutral' },
  approved: { text: '已批准', cls: 'badge-ok' },
  joining: { text: '等群主审批', cls: 'badge-warn' },
  joined: { text: '考核中', cls: 'badge-warn' },
  kept: { text: '保留', cls: 'badge-ok' },
  left: { text: '已退出', cls: 'badge-fail' },
  rejected: { text: '已否决', cls: 'badge-neutral' },
  blacklisted: { text: '黑名单', cls: 'badge-fail' },
  failed: { text: '加入失败', cls: 'badge-fail' },
};

const TABS: Array<{ key: string; label: string; statuses: JoinPlanStatus[] | null }> = [
  { key: 'pipeline', label: '进行中', statuses: ['candidate', 'approved', 'joining', 'joined'] },
  { key: 'kept', label: '已保留', statuses: ['kept'] },
  { key: 'out', label: '退出 / 否决 / 黑名单', statuses: ['left', 'rejected', 'blacklisted', 'failed'] },
];

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function ExpansionPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('pipeline');
  const [status, setStatus] = useState<'' | JoinPlanStatus>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const stats = useQuery({
    queryKey: ['expansion', 'stats'],
    queryFn: () => api.get<ExpansionStats>(Routes.admin.expansionStats),
    refetchInterval: 30_000,
  });
  const s = stats.data;

  const run = useMutation({
    mutationFn: () => api.post<ExpansionRunResponse>(Routes.admin.expansionRun, { ignoreWindow: false }),
    onSuccess: (r) => {
      if (r.skippedReason === 'disabled') toastInfo('扩散已关闭（系统配置 → 扩散计划）');
      else if (r.skippedReason === 'outside_join_window') toastInfo(`已打分 ${r.scored}、考核 ${r.evaluated}；当前不在加群时段，未加群`);
      else toastSuccess(`打分 ${r.scored} · 批准 ${r.promoted} · 加入 ${r.joined} · 待群主审批 ${r.pending} · 失败 ${r.failed} · 考核 ${r.evaluated}（保留 ${r.kept} / 退出 ${r.left}）`);
      qc.invalidateQueries({ queryKey: ['expansion'] });
      qc.invalidateQueries({ queryKey: ['discovery'] });
    },
    onError: (e) => toastError(e),
  });

  const statuses = TABS.find((t) => t.key === tab)!.statuses;
  const list = useInfiniteQuery({
    queryKey: ['expansion', 'plans', tab, status],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      // The API filters one status at a time; a tab is a union → fetch per status when no explicit filter.
      if (status) {
        return api.get<{ items: JoinPlanSummary[]; nextCursor: string | null }>(`${Routes.admin.expansionPlans}${qs({ status, cursor: pageParam, limit: 100 })}`);
      }
      const pages = await Promise.all(
        (statuses ?? []).map((st) => api.get<{ items: JoinPlanSummary[]; nextCursor: string | null }>(`${Routes.admin.expansionPlans}${qs({ status: st, limit: 100 })}`)),
      );
      return { items: pages.flatMap((p) => p.items).sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.score - a.score), nextCursor: null };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

  const decide = useMutation({
    mutationFn: (v: { id: string; action: string; note?: string }) =>
      api.post<JoinPlanSummary>(Routes.admin.expansionPlanDecide(v.id), { action: v.action, note: v.note }),
    onSuccess: (p) => {
      toastSuccess(`已更新：${STATUS[p.status].text}`);
      qc.invalidateQueries({ queryKey: ['expansion'] });
    },
    onError: (e) => toastError(e),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">扩散计划</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            资源池里能发言的群按"来源群产出 / 被提到次数 / 规模 / 关键词 / 跳数"打分。
            {s?.mode === 'auto' ? '当前是自动模式：分数达标的自动批准；' : '当前是人工模式：只打分，等你批准；'}
            你随时可以批准、置顶、否决或拉黑。加入按预算限速，7 天后按线索产出考核，不达标自动退群并拉黑。参数在「系统配置 → 扩散计划」。
          </p>
        </div>
        <button className="btn-secondary text-xs" disabled={run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? '执行中…' : '立即跑一轮'}
        </button>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card title={`今日加群 ${s.joinsToday} / ${s.globalDailyJoins}`} sub={`还有加群额度的观察号 ${s.accountsWithBudget} / ${s.observersTotal}`} />
          <Card title={`候选 ${s.byStatus['candidate'] ?? 0}`} sub={`已批准待加 ${s.byStatus['approved'] ?? 0} · 等群主 ${s.byStatus['joining'] ?? 0}`} />
          <Card title={`考核中 ${s.byStatus['joined'] ?? 0}`} sub={`保留 ${s.byStatus['kept'] ?? 0} · 退出 ${s.byStatus['left'] ?? 0}`} />
          <Card
            title={s.enabled ? `模式：${s.mode === 'auto' ? '自动' : '人工'}` : '扩散已关闭'}
            sub={`上次 ${s.lastTickAt ? new Date(s.lastTickAt).toLocaleTimeString() : '—'} · 下次 ${s.nextTickAt ? new Date(s.nextTickAt).toLocaleTimeString() : '—'}`}
            warn={!s.enabled}
          />
        </div>
      )}

      <div className="flex gap-1 border-b items-center">
        {TABS.map((t) => (
          <button key={t.key} className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === t.key ? 'border-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            onClick={() => { setTab(t.key); setStatus(''); }}>
            {t.label}
          </button>
        ))}
        <select className="input w-40 ml-auto text-xs" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="">本页全部状态</option>
          {(statuses ?? []).map((st) => <option key={st} value={st}>{STATUS[st].text}</option>)}
        </select>
      </div>

      <div className="card overflow-x-auto">
        {list.isLoading ? (
          <div className="text-sm text-slate-500">加载中…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500">没有记录。资源池里出现能发言的群后，下一轮调度会自动打分。</div>
        ) : (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500 uppercase border-b">
                <th className="py-2 pr-3">群</th>
                <th className="py-2 pr-3">成员</th>
                <th className="py-2 pr-3">分数</th>
                <th className="py-2 pr-3">状态</th>
                <th className="py-2 pr-3">观察号</th>
                <th className="py-2 pr-3">时间</th>
                <th className="py-2 pr-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <Fragment key={p.id}>
                  <tr className="border-b last:border-0 cursor-pointer hover:bg-slate-50" onClick={() => setExpanded((c) => (c === p.id ? null : p.id))}>
                    <td className="py-2 pr-3 max-w-[22rem]">
                      <div className="font-medium truncate" title={p.chat.title ?? ''}>
                        {p.purpose === 'coverage' ? <span className="badge badge-ok mr-1" title={`补位：目标 ${p.targetObservers ?? '?'} 个观察号`}>补位</span> : p.pinned && <span className="badge badge-warn mr-1">置顶</span>}
                        {p.chat.title ?? '（未命名）'}
                      </div>
                      <div className="font-mono text-slate-500">{p.chat.username ? `@${p.chat.username}` : p.chat.inviteHash ? `+${p.chat.inviteHash}` : ''}</div>
                    </td>
                    <td className="py-2 pr-3">{p.chat.membersCount?.toLocaleString() ?? '—'}</td>
                    <td className="py-2 pr-3 font-mono">{p.score.toFixed(3)}</td>
                    <td className="py-2 pr-3">
                      <span className={`badge ${STATUS[p.status].cls}`} title={p.lastError ?? ''}>{STATUS[p.status].text}</span>
                      {p.decidedBy === 'operator' && <span className="ml-1 text-slate-400">人工</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-slate-500">{p.assignedAccounts.length ? `${p.assignedAccounts.length}${p.targetObservers ? `/${p.targetObservers}` : ''} 个` : '—'}<div className="text-[10px] truncate max-w-[12rem]">{p.assignedAccounts.map((a) => a.phone).join(', ')}</div></td>
                    <td className="py-2 pr-3 text-slate-500">
                      {p.status === 'joined' && p.evaluateAt ? `考核 ${new Date(p.evaluateAt).toLocaleDateString()}` : p.joinedAt ? `加入 ${new Date(p.joinedAt).toLocaleDateString()}` : new Date(p.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-3 space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {['candidate', 'rejected', 'failed', 'blacklisted', 'left'].includes(p.status) && (
                        <>
                          <button className="btn-secondary text-xs" onClick={() => decide.mutate({ id: p.id, action: 'approve' })}>批准</button>
                          <button className="btn-secondary text-xs" onClick={() => decide.mutate({ id: p.id, action: 'pin' })}>置顶</button>
                        </>
                      )}
                      {['candidate', 'approved', 'joining'].includes(p.status) && (
                        <button className="btn-secondary text-xs" onClick={() => decide.mutate({ id: p.id, action: 'reject' })}>否决</button>
                      )}
                      {['joined', 'kept'].includes(p.status) && (
                        <button className="btn-danger text-xs" onClick={() => { if (confirm('退出这个群并拉黑？')) decide.mutate({ id: p.id, action: 'leave' }); }}>退群</button>
                      )}
                      {p.status !== 'blacklisted' && p.status !== 'left' && (
                        <button className="btn-secondary text-xs" onClick={() => decide.mutate({ id: p.id, action: 'blacklist' })}>拉黑</button>
                      )}
                    </td>
                  </tr>
                  {expanded === p.id && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="py-2 px-3">
                        <PlanDetail p={p} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Card(props: { title: string; sub?: string; warn?: boolean }) {
  return (
    <div className={`card ${props.warn ? 'border-amber-300 bg-amber-50' : ''}`}>
      <div className="text-sm font-medium">{props.title}</div>
      {props.sub && <div className="text-[11px] text-slate-500 mt-1">{props.sub}</div>}
    </div>
  );
}

function PlanDetail({ p }: { p: JoinPlanSummary }) {
  const sj = p.scoreJson as Record<string, unknown>;
  const sources = (sj['sources'] as Array<{ groupTitle: string; leadsPer100Senders: number; links: number }> | undefined) ?? [];
  const ev = p.evaluationJson as Record<string, unknown> | null;
  return (
    <div className="grid md:grid-cols-3 gap-4 text-xs">
      <div>
        <div className="font-medium mb-1">分数构成</div>
        <ul className="space-y-0.5">
          {(['sourceQuality', 'mentions', 'size', 'keywords', 'proximity'] as const).map((k) => (
            <li key={k} className="flex justify-between"><span className="text-slate-500">{{ sourceQuality: '来源群产出', mentions: '被提到', size: '规模', keywords: '关键词', proximity: '跳数' }[k]}</span><span className="font-mono">{String(sj[k] ?? '—')}</span></li>
          ))}
          {Array.isArray(sj['boostHits']) && (sj['boostHits'] as string[]).length > 0 && (
            <li className="text-slate-500">命中：{(sj['boostHits'] as string[]).join('、')}</li>
          )}
          {typeof sj['reason'] === 'string' && <li className="text-red-700">排除原因：{sj['reason']}</li>}
        </ul>
      </div>
      <div>
        <div className="font-medium mb-1">从哪些群发现的</div>
        {sources.length === 0 ? <div className="text-slate-500">无记录</div> : (
          <ul className="space-y-0.5">
            {sources.map((s, i) => (
              <li key={i} className="flex justify-between"><span className="truncate mr-2">{s.groupTitle}</span><span className="font-mono text-slate-500">{s.links} 条 · 线索 {s.leadsPer100Senders} · 带出群 {(s as { chatsPer100Senders?: number }).chatsPer100Senders ?? 0}</span></li>
            ))}
          </ul>
        )}
        <div className="mt-2 text-slate-500">被提到 {p.chat.discoveredCount} 次 · 第 {p.chat.depth} 跳 · {p.chat.link && <a className="underline" href={p.chat.link} target="_blank" rel="noreferrer">打开链接</a>}</div>
      </div>
      <div>
        <div className="font-medium mb-1">考核 / 备注</div>
        {ev ? (
          <ul className="space-y-0.5">
            <li>观察 {String(ev['days'])} 天 · 消息 {String(ev['msgs'])} · 发言人 {String(ev['senders'])} · 过 L1 {String(ev['l1Passed'])} · 线索 {String(ev['leads'])} · 带出新群 {String(ev['newChats'] ?? 0)}</li>
            <li className="text-slate-500">结论：{String(ev['verdict'])}</li>
          </ul>
        ) : <div className="text-slate-500">{p.status === 'joined' && p.evaluateAt ? `${new Date(p.evaluateAt).toLocaleString()} 考核` : '—'}</div>}
        {p.lastError && <div className="mt-1 text-red-700">最近错误：{p.lastError}</div>}
        {p.note && <div className="mt-1">备注：{p.note}</div>}
      </div>
    </div>
  );
}
